import { useMemo, useEffect, useRef } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import ContentHeader from "src/widgets/contentHeader/ContentHeader";
import LoaderWrapper from "src/widgets/loaderWrapper/LoaderWrapper";
import TanstackTable from "src/widgets/table/tanstackTable";
import { useGetSupportTicketsQuery, useGetUnreadTicketStatsQuery, useCheckSupportModuleStatusQuery, SupportTicket, CRITICALITY_IDS } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { useCheckIndorIDAuthQuery } from "src/api/rtk/indorsoftRu/slices/indorIDAuthApiSlice";
import { formatBitrixMoscowToUserDateOrTimeShort } from "src/shared/format/date";
import { useNavigate } from "react-router-dom";
import { getIndorImageSrc, IndorHuges } from "src/features/IndorImages/IndorImages";
import css from "./SupportTicketListPage.module.scss";
import { getItemWebStorage } from "src/features/webStorageSaver";

const columnHelper = createColumnHelper<SupportTicket>();

function formatLastMessageDate(dateString: string | null): string {
    return formatBitrixMoscowToUserDateOrTimeShort(dateString);
}

function getTicketStatus(ticket: SupportTicket): { text: string; className: string } {
    // 1. Закрыт
    if (ticket.DATE_CLOSE) {
        return { text: "Закрыто", className: css.statusClosed };
    }

    // 2. Последнее сообщение от поддержки
    if (ticket.LAST_MESSAGE_BY_SUPPORT_TEAM === "Y") {
        // backend уже сообщает, есть ли непрочитанные ответы поддержки
        if (ticket.HAS_UNREAD_SUPPORT_MESSAGES === "Y") {
            return { text: "Новое сообщение", className: css.statusNewMessage };
        }
        return { text: "Прочитано", className: css.statusRead };
    }

    // 3. Последнее сообщение от пользователя — поддержка должна ответить
    if (ticket.LAST_MESSAGE_BY_SUPPORT_TEAM === "N") {
        return { text: "Ожидание ответа", className: css.statusWaiting };
    }

    // 4. Базовый вариант — открыто
    return { text: "Открыто", className: css.statusOpen };
}

/**
 * Определяет иконку критичности на основе CRITICALITY_NAME
 * Низкая критичность -> HelpSendQuestion
 * Средняя или критичная -> HelpSendBugs
 */
function getCriticalityIcon(criticalityId: string | undefined): IndorHuges {
    // Проверяем на низкую критичность
    if (criticalityId === `${CRITICALITY_IDS.low}`) {
        return IndorHuges.HelpSendQuestion;
    }

    // Для средней и высокой используем HelpSendBugs
    return IndorHuges.HelpSendBugs;
}

export default function SupportTicketListPage() {
    const navigate = useNavigate();
    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const isSupportEnabledInConfig = currentConfig?.support?.enabled ?? false;
    
    // Проверка активности модуля поддержки
    const { data: supportModuleStatus, isLoading: isCheckingModuleStatus } = useCheckSupportModuleStatusQuery(undefined, {
        skip: !isSupportEnabledInConfig,
        refetchOnMountOrArgChange: true,
    });
    
    const { data, isLoading, isError, error, isFetching, isSuccess } = useGetSupportTicketsQuery(undefined, {
        skip: !isSupportEnabledInConfig || !supportModuleStatus?.isActive, // Пропускаем запрос, если модуль не активен
        pollingInterval: 10000, // Polling каждые 10 секунд для обновления списка тикетов (только когда пользователь на странице)
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });
    const tickets = data?.tickets ?? [];

    // Проверка авторизации и перезапрос статистики непрочитанных при заходе на страницу
    const { data: authData, isLoading: isCheckingAuth } = useCheckIndorIDAuthQuery(undefined, { skip: !isSupportEnabledInConfig });
    const { refetch: refetchUnreadStats } = useGetUnreadTicketStatsQuery(undefined, {
        skip: !isSupportEnabledInConfig || !authData?.authorized,
    });

    // Редирект на страницу "Справка" при отсутствии авторизации
    useEffect(() => {
        // Ждем завершения проверки авторизации
        if (!isCheckingAuth && authData && !authData.authorized) {
            navigate('/app/help');
        }
    }, [authData, isCheckingAuth, navigate]);

    // Отслеживаем состояние запроса для синхронизации статистики в меню
    const prevIsFetchingRef = useRef<boolean>(false);
    const isInitialMountRef = useRef<boolean>(true);

    // Синхронизация: при завершении каждого успешного обновления списка тикетов обновляем статистику в меню
    useEffect(() => {
        // Пропускаем, если пользователь не авторизован или refetch недоступен
        if (!authData?.authorized || !refetchUnreadStats) {
            return;
        }

        // Пропускаем первую загрузку (она обрабатывается отдельным useEffect)
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            prevIsFetchingRef.current = isFetching;
            return;
        }

        // Отслеживаем завершение запроса: когда isFetching переходит с true на false и запрос успешен
        if (prevIsFetchingRef.current && !isFetching && isSuccess && data) {
            // Запрос завершился успешно - синхронизируем статистику
            try {
                if (typeof refetchUnreadStats === 'function') {
                    refetchUnreadStats();
                }
            } catch (error) {
                // Игнорируем ошибки при обновлении статистики
            }
        }

        prevIsFetchingRef.current = isFetching;
    }, [isFetching, isSuccess, data, authData?.authorized, refetchUnreadStats]);

    useEffect(() => {
        // Перезапрашиваем статистику непрочитанных при монтировании страницы
        // Вызываем только если пользователь авторизован и refetch доступен
        if (authData?.authorized && refetchUnreadStats) {
            try {
                // Проверяем, что refetch является функцией перед вызовом
                if (typeof refetchUnreadStats === 'function') {
                    refetchUnreadStats();
                }
            } catch (error) {
                // Игнорируем ошибки, если запрос еще не был запущен
                // Это нормально, если запрос был пропущен из-за skip
            }
        }
    }, [authData?.authorized, refetchUnreadStats]); // Выполняем при изменении авторизации

    const columns = useMemo(
        () => [
            columnHelper.accessor("ID", {
                header: "Номер",
                cell: (info) => `№${info.getValue()}`,
                meta: { width: "100px", cellAlign: "center", headerAlign: "center" },
            }),
            columnHelper.display({
                id: "criticality",
                header: "Тип",
                cell: ({ row }) => {
                    const iconType = getCriticalityIcon(row.original.CRITICALITY_ID);
                    return (
                        <img
                            src={getIndorImageSrc(iconType, 64, "IndorHuges")}
                            alt="Критичность"
                            className={css.criticalityIcon}
                        />
                    );
                },
                meta: { width: "100px", cellAlign: "center", headerAlign: "center" },
            }),
            columnHelper.accessor("LAST_MESSAGE_DATE", {
                header: "Изменено",
                cell: (info) => formatLastMessageDate(info.getValue()),
                meta: { width: "165px", cellAlign: "center", headerAlign: "center" },
            }),
            columnHelper.accessor("TITLE", {
                header: "Заголовок",
                cell: (info) => info.getValue(),
            }),
            columnHelper.display({
                id: "status",
                header: "Состояние",
                cell: ({ row }) => {
                    const status = getTicketStatus(row.original);
                    return (
                        <span className={`${css.tableStatus} ${status.className}`}>
                            {status.text}
                        </span>
                    );
                },
                meta: { width: "180px", cellAlign: "center", headerAlign: "center" },
            }),
        ],
        []
    );

    // Проверяем конкретную ошибку о неактивном модуле поддержки
    let errorMessage = isError ? "Ошибка загрузки данных" : null
    if (isError && error) {
        const errorText = typeof error === 'string'
            ? error
            : (error as any)?.data?.error || (error as any)?.data?.message || (error as any)?.error || JSON.stringify(error);

        if (typeof errorText === 'string' && errorText.includes('Module indorsoft.support is not installed')) {
            errorMessage = "Служба тех. поддержки временно недоступна. Обратитесь к администратору системы.";
        }
    }

    // Если модуль не активен, показываем предупреждение
    if (!isSupportEnabledInConfig) {
        return (
            <>
                <ContentHeader title="Список обращений" />
                <div className={css.supportTicketListContainer}>
                    <div className={css.errorMessage}>
                        Модуль тех. поддержки отключён в конфигурации базы.
                    </div>
                </div>
            </>
        );
    }

    if (isCheckingModuleStatus) {
        return (
            <>
                <ContentHeader title="Список обращений" />
                <div className={css.supportTicketListContainer}>
                    <LoaderWrapper isLoading={true}>
                        <div></div>
                    </LoaderWrapper>
                </div>
            </>
        );
    }

    if (!supportModuleStatus?.isActive) {
        return (
            <>
                <ContentHeader title="Список обращений" />
                <div className={css.supportTicketListContainer}>
                    <div className={css.errorMessage}>
                        Служба тех. поддержки временно недоступна. Обратитесь к администратору системы.
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <ContentHeader title="Список обращений" />
            <div className={css.supportTicketListContainer}>
                <LoaderWrapper isLoading={isLoading}>
                    {errorMessage ? (
                        <div className={css.errorMessage}>{errorMessage}</div>
                    ) : (
                        <TanstackTable
                            data={tickets}
                            columns={columns}
                            emptyMessage="Нет обращений"
                            enableSorting={false}
                            onRowClick={(ticket) => navigate(`${ticket.ID}`)}
                        />
                    )}
                </LoaderWrapper>
            </div>
        </>
    );
}
